import React, { useEffect, useRef, useState } from "react";
import { Bell, Check, Loader2, X } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * NotifyMeModal — small email-capture drawer used by the "Notify me"
 * pill on each Coming-Soon OEM chip in the About section.
 *
 * Fully controlled: parent opens with { open, make } and closes via
 * onClose. Idempotent on the backend, so double-submits are safe.
 */
export const NotifyMeModal = ({ open, make, onClose }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  // Focus the email input the moment the drawer opens.
  useEffect(() => {
    if (open) {
      setState("idle");
      setEmail("");
      setErr("");
      setResult(null);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open, make]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = (email || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      setErr("Please enter a valid email address.");
      return;
    }
    setErr("");
    setState("loading");
    try {
      const r = await fetch(`${API_BASE}/api/oem/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, make, source: "landing_about_grid" }),
      });
      if (!r.ok) throw new Error("Notify signup failed");
      const data = await r.json();
      setResult(data);
      setState("done");
    } catch (e2) {
      setErr("Something went wrong. Please try again in a moment.");
      setState("error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="notify-me-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[#0a0a0a] border border-white/10 rounded-t-2xl sm:rounded-2xl p-6 relative shadow-2xl"
        style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          aria-label="Close"
          data-testid="notify-me-close"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>

        {state !== "done" ? (
          <>
            <div className="flex items-center gap-2 mb-1 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              <Bell className="w-3 h-3" style={{ color: "#39FF14" }} strokeWidth={2.4} />
              Coming-Soon waitlist
            </div>
            <h3 className="font-display text-2xl text-white font-semibold leading-tight tracking-tight">
              Get pinged the day{" "}
              <span className="brand-text-gradient">{make}</span> goes live.
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed mt-2">
              One email, zero spam. We'll let you know the exact moment
              {" "}
              {make} telemetry becomes available in EVIQ — no marketing
              blasts in between.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-3">
              <input
                ref={inputRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-white/30"
                data-testid="notify-me-email"
                disabled={state === "loading"}
              />
              {err && (
                <div className="text-xs text-[#FF6B57]" data-testid="notify-me-error">
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={state === "loading"}
                className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-medium text-sm rounded-lg py-2.5 hover:bg-zinc-200 transition-colors disabled:opacity-60"
                data-testid="notify-me-submit"
              >
                {state === "loading" ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.4} />
                    Adding you…
                  </>
                ) : (
                  <>
                    <Bell className="w-3.5 h-3.5" strokeWidth={2.4} />
                    Notify me when {make} launches
                  </>
                )}
              </button>
              <p className="text-[10px] text-zinc-600 leading-relaxed">
                By joining you agree to receive a single launch email from
                EVIQ. We never sell, share, or resell email addresses.
              </p>
            </form>
          </>
        ) : (
          <div data-testid="notify-me-success">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
              style={{ background: "rgba(57,255,20,0.14)", border: "1px solid rgba(57,255,20,0.35)" }}
            >
              <Check className="w-5 h-5" style={{ color: "#39FF14" }} strokeWidth={2.4} />
            </div>
            <h3 className="font-display text-2xl text-white font-semibold leading-tight tracking-tight">
              {result?.already_on_list ? "You're already on the list." : "You're in."}
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed mt-2">
              We'll email you the moment {result?.make || make} telemetry
              is available. In the meantime,
              {" "}
              <span className="text-white font-medium">
                {result?.waitlist_size ?? "—"}
              </span>{" "}
              other owner{result?.waitlist_size === 1 ? "" : "s"} are waiting on the same launch.
            </p>
            <button
              onClick={onClose}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 border border-white/15 text-white font-medium text-sm rounded-lg py-2.5 hover:bg-white/[0.05] transition-colors"
              data-testid="notify-me-done"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotifyMeModal;
